import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Lightbulb, Heart, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';

const commitments = [
  {
    icon: Target,
    title: 'Commitment to Quality',
    description: 'Excellence in every detail, exceeding expectations through rigorous standards and continuous improvement.',
    details: 'Our commitment to quality is reflected in every project we undertake. We maintain the highest standards of code quality, performance optimization, and user experience design.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80'
  },
  {
    icon: Lightbulb,
    title: 'Innovation at the Core',
    description: 'Pioneering tech solutions that drive transformation and create lasting impact.',
    details: 'We stay at the forefront of technological advancement, constantly exploring new ways to solve complex challenges and drive digital innovation.',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80'
  },
  {
    icon: Heart,
    title: 'Customer-Centric Focus',
    description: "Tailored strategies for business growth, putting our clients' success first.",
    details: "Understanding our clients' needs and objectives is at the heart of everything we do. We build lasting partnerships that drive mutual success.",
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80'
  },
  {
    icon: Shield,
    title: 'Sustainability & Future-Readiness',
    description: 'Building long-term, scalable solutions that stand the test of time.',
    details: 'Our solutions are designed with the future in mind, ensuring scalability, maintainability, and environmental responsibility.',
    image: 'https://images.unsplash.com/photo-1473186578172-c141e6798cf4?auto=format&fit=crop&q=80'
  }
];

const OurCommitments = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        handleNext();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [currentSlide, isAnimating]);

  const handlePrevious = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === 0 ? commitments.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === commitments.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <section className="py-16 md:py-20 bg-black">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-display font-semibold text-white mb-12"
        >
          Our Core Commitments
        </motion.h2>

        {/* Carousel Layout */}
        <div className="relative overflow-hidden rounded-xl">
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60 backdrop-blur-sm z-10" />
          
          {/* Background Image */}
          <motion.div
            initial={false}
            animate={{ 
              scale: 1.1,
              transition: { duration: 6, ease: "easeOut" }
            }}
            className="absolute inset-0"
          >
            <motion.img
              key={currentSlide}
              src={commitments[currentSlide].image}
              alt={commitments[currentSlide].title}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Content */}
          <div className="relative z-20 px-6 py-12 md:p-16">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-xl bg-accent/20 flex items-center justify-center mb-6">
                    {React.createElement(commitments[currentSlide].icon, {
                      className: "w-8 h-8 text-accent"
                    })}
                  </div>
                  
                  {/* Title & Description */}
                  <h3 className="text-3xl font-display font-semibold text-white mb-4">
                    {commitments[currentSlide].title}
                  </h3>
                  <p className="text-xl text-gray-300 mb-6">
                    {commitments[currentSlide].description}
                  </p>
                  <p className="text-gray-400">
                    {commitments[currentSlide].details}
                  </p>
                </motion.div>

                {/* Navigation */}
                <div className="flex flex-col items-end justify-between h-full">
                  <div className="flex items-center gap-4 self-end">
                    <button
                      onClick={handlePrevious}
                      className={cn(
                        "p-2 rounded-full bg-white/10 backdrop-blur-sm transition-all duration-300",
                        "hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      )}
                      disabled={isAnimating}
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={handleNext}
                      className={cn(
                        "p-2 rounded-full bg-white/10 backdrop-blur-sm transition-all duration-300",
                        "hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      )}
                      disabled={isAnimating}
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </div>

                  {/* Dots */}
                  <div className="flex gap-2 mt-8">
                    {commitments.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (!isAnimating && currentSlide !== index) {
                            setIsAnimating(true);
                            setCurrentSlide(index);
                            setTimeout(() => setIsAnimating(false), 500);
                          }
                        }}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all duration-300",
                          currentSlide === index 
                            ? "bg-accent w-8" 
                            : "bg-white/20 hover:bg-white/40"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurCommitments;